import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { useMediaQuery, Fab, Drawer } from '@material-ui/core';
import { styled, useTheme, Theme } from '@material-ui/core/styles';
import { Cancel, Chat } from '@material-ui/icons';
import {
  CONTEXT_TREATMENTS,
  DEFAULT_VOICE_SETTINGS,
  KEY_LOCAL_ASSIGNED_SURVEYS,
  KEY_LOCAL_ASSIGN_TREATMENT,
  KEY_LOCAL_NO_LOG_KEY,
  KEY_SESSION_ORGANIZATION_ID,
} from 'CONST';
import { BANNER_STATUS, MENU_STATUS, TOOGLE_MENU_STATUS } from 'gql';
import { ChatBot_History, ChatBot_LogKey } from 'gql/server/chatbot';
import {
  useCheckLogin,
  useCustomPermission,
  useEffectOnlyOnce,
  useHandlePathExists,
  useUpdateMeClient,
} from 'hooks';
import { Chatbot } from 'modules/chatbot/components/Chatbot';
import MobileChatBot from 'modules/chatbot/components/MobileChatbot';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ChatbotDrawerContext } from 'share/context';
import {
  getApiBaseUrl,
  getLocalStorage,
  getSessionStorage,
  setDataLocalStorage,
  setDataSessionStorage,
} from 'share/utils';
import {
  ChatBotHistoryKeyQuery,
  ChatBotHistoryQuery,
  QueryChatBotArgs,
  QueryChatBotHistoryArgs,
} from 'types.d';

function cleanTrailingSSE(text: string): string {
  return text
    .replace(/event:\s*done\s*data:\s*\[DONE\]/gi, '')
    .replace(/event:\s*done/gi, '')
    .replace(/data:\s*\[DONE\]/gi, '')
    .replace(/\[DONE\]/gi, '')
    .trim();
}

function extractArrayString(jsonSection: string): string | null {
  const start = jsonSection.indexOf('[');
  if (start === -1) return null;
  let stack = 0;
  for (let i = start; i < jsonSection.length; i++) {
    if (jsonSection[i] === '[') stack++;
    else if (jsonSection[i] === ']') stack--;
    if (stack === 0) {
      return jsonSection.substring(start, i + 1);
    }
  }
  return null;
}

export interface ChatbotExtraction {
  answer: string;
  articles: any[];
  responseId: string | null;
}

export function extractBotAnswerAndJson(raw: string): ChatbotExtraction {
  const cleanedLines = raw
    .split('\n')
    .filter(line => !line.startsWith('[info]'));
  const allCleaned = cleanedLines.join('\n');
  const [main, jsonSection] = allCleaned.split('<<<FULL_JSON>>>');

  let articles: any[] = [];
  let responseId: string | null = null;

  // Extract [ID] line if present
  let answer = main?.trim() ?? '';
  const idMatch = answer.match(/\[ID\]\s*([a-f0-9]+)/i);
  if (idMatch) {
    responseId = idMatch[1];
    // Remove the [ID] line from answer (whole line or inline)
    answer = answer.replace(/\[ID\].*/g, '').trim();
  }
  answer = cleanTrailingSSE(answer);

  if (jsonSection) {
    try {
      const arrayString = extractArrayString(jsonSection);
      if (arrayString) {
        const arr = JSON.parse(arrayString);
        if (Array.isArray(arr)) {
          articles = arr.map((obj: any) => ({
            name: obj.name,
            slug: obj.slug,
            shortDescription: obj.shortDescription,
            urlSharing: obj.urlSharing,
            mainImage: obj.mainImage,
          }));
        }
      }
    } catch (err) {
      console.error('JSON parse error:', err);
    }
  }

  return {
    answer,
    articles,
    responseId,
  };
}

export async function streamChatApi(
  organization: string,
  query: string,
  nologkey: string,
  onChunk: (chunk: string) => void,
  onDone?: () => void,
  onError?: (err: any) => void,
  onInfo?: (infoLines: string[] | null) => void, // Array of info lines or null to clear
) {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/chat/stream?q=${encodeURIComponent(
    query,
  )}&nologkey=${encodeURIComponent(nologkey)}&organization=${encodeURIComponent(
    organization,
  )}`;

  try {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Network error');

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let done = false;
    let currentText = '';
    let gotFirstResponse = false;

    let infoLines: string[] = []; // Track all info messages

    while (!done) {
      const { value, done: streamDone } = await reader.read();
      done = streamDone;
      if (value) {
        const chunk = decoder.decode(value, { stream: !done });
        currentText += chunk;

        // Find ALL [info] lines up to now
        const infoMatches = Array.from(
          currentText.matchAll(/^\[info\] (.*)$/gm),
        );
        if (infoMatches.length > infoLines.length) {
          // Only add new info lines
          for (let i = infoLines.length; i < infoMatches.length; i++) {
            infoLines.push(infoMatches[i][1]);
          }
          if (onInfo) onInfo([...infoLines]);
        }

        // When answer arrives, clear info
        const { answer } = extractBotAnswerAndJson(currentText);
        if (answer && answer.trim() !== '' && !gotFirstResponse) {
          if (onInfo) onInfo(null); // Hide/clear all info
          gotFirstResponse = true;
          infoLines = []; // Reset for the next request
        }

        onChunk(chunk);
      }
    }
    if (onInfo) onInfo(null); // Final clear
    if (onDone) onDone();
  } catch (err) {
    if (onInfo) onInfo(null);
    if (onError) onError(err);
  }
}

export const DrawerStyled = styled(Drawer)(
  ({
    banner,
    surveyOpened,
    theme,
  }: {
    banner: boolean | undefined;
    surveyOpened: boolean;
    theme: Theme;
  }) => ({
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    width: surveyOpened ? '800px' : '400px',
    paddingTop: banner ? '65px' : '65px',
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: surveyOpened ? '800px' : '400px',
      boxSizing: 'border-box',
      paddingTop: banner ? '65px' : '65px',
    },
  }),
);

const FloatingChatStyled = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(8),
  right: theme.spacing(2),
  zIndex: 11112,
}));

export type ContextData = {
  name: string;
  title: string;
  content: string;
  relevancyScore: string;
  treatmentId: string;
};

type Props = {
  chatbotDrawer: boolean;
  setChatbotDrawer: any;
};

export const ChatbotDrawer: React.FC<Props> = ({
  chatbotDrawer,
  setChatbotDrawer,
}) => {
  const theme = useTheme();

  const isLogin = useCheckLogin();

  const location = useLocation();

  const history = useHistory();

  const { meClient, handleUpdateMeClient } = useUpdateMeClient();

  const { enqueueSnackbar } = useSnackbar();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isUser, isPatient } = useCustomPermission();

  const [toogleMenuStatus] = useMutation(TOOGLE_MENU_STATUS);

  const { data: dataBannerStatus } = useQuery(BANNER_STATUS);

  const { data: dataMenuStatus } = useQuery(MENU_STATUS);

  const queryParams: any = new URLSearchParams(location.search);

  const chatBodyRef = useRef<HTMLDivElement>(null);

  const [message, setMessage] = useState('');

  const [infoLines, setInfoLines] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);

  const [surveyOpened, setSurveyOpened] = useState(false);

  const treatmentAssigneeEmail =
    JSON.parse(getLocalStorage(KEY_LOCAL_ASSIGN_TREATMENT)!) != null &&
    JSON.parse(getLocalStorage(KEY_LOCAL_ASSIGN_TREATMENT)!).length > 0
      ? JSON.parse(getLocalStorage(KEY_LOCAL_ASSIGN_TREATMENT)!).find(
          (treatment: { email: any }) => treatment.email,
        )?.email
      : null;

  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [assignedSurvey, setAssignedSurvey] = useState<any>(false);
  const [surveyInitiated, setSurveyInitiated] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [inputMessage, setInputMessage] = useState('');
  const [queryMessage, setQueryMessage] = useState('');
  const [displayChatbot, setDisplayChatBot] = useState(false);
  const [logKey, setLogKey] = useState(
    treatmentAssigneeEmail != null && treatmentAssigneeEmail != 'null'
      ? treatmentAssigneeEmail
      : getLocalStorage(KEY_LOCAL_NO_LOG_KEY)!,
  );
  const [historyKey, setHistoryKey] = useState<any>(null);
  const [responseAdded, setResponseAdded] = useState(false);

  const [
    treatmentContextFilteredIds,
    setTreatmentContextFilteredIds,
  ] = useState<string[]>(() => {
    const storedValue = getLocalStorage(CONTEXT_TREATMENTS);
    return storedValue ? storedValue.split(',') : [];
  });

  let params: QueryChatBotHistoryArgs['params'] = {
    nologkey: logKey,
  };

  var voice_settings = JSON.parse(getLocalStorage(DEFAULT_VOICE_SETTINGS)!);

  const pathExists = useHandlePathExists();

  const [getlogkey] = useLazyQuery<ChatBotHistoryKeyQuery>(ChatBot_LogKey, {
    fetchPolicy: 'cache-and-network',
    onCompleted(data: any) {
      setHistoryKey(data.getHistoryKey);
    },
  });

  const { loading: loadingd, data: historydata } = useQuery<
    ChatBotHistoryQuery,
    QueryChatBotHistoryArgs
  >(ChatBot_History, {
    variables: {
      params,
    },
    fetchPolicy: 'cache-and-network',
    onCompleted(data) {
      if (data != null && data.history != null) {
        const tempMessages: React.SetStateAction<any[]> = [];
        var temporarySurveys: any[] = [];

        data.history.forEach((msg: any) => {
          if (msg.role == 'user') {
            tempMessages.push({ text: msg.content, type: 'user' });
          } else {
            tempMessages.push({
              text: msg.content,
              responseId: msg.responseId,
              type: 'bot',
              like: msg.like,
              comment: msg.comment,
              history: true,
              isMarkdown: true,
              pitch: voice_settings.pitch,
              rate: voice_settings.rate,
              //vol: 0.8,
              voice: voice_settings.voice,
              settings: false,
              played: undefined,
              relatedArticles:
                msg.relatedArticles?.length > 0 ? msg.relatedArticles : [],
            });
          }
        });
        if (messages.length == 0 && inputMessage) {
          tempMessages.push({ text: inputMessage, type: 'user' });
        }
        if (data.history?.length > 0) {
          data.history[data.history?.length - 1].relatedArticles.forEach(
            (rt: { survey: any[] | null }) => {
              if (
                (!isLogin && rt?.survey != null && rt?.survey.length > 0) ||
                (rt?.survey != null &&
                  rt?.survey.length > 0 &&
                  isLogin &&
                  (isPatient || isUser))
              ) {
                rt.survey.forEach(survey => {
                  temporarySurveys.push({
                    surveyId: survey._id,
                    name: survey.name,
                    slug: survey.slug,
                    survey: survey.surveyData,
                  });
                });
              }
            },
          );
        }
        temporarySurveys = getAssignedSurveys(temporarySurveys);
        if (temporarySurveys.length > 0) {
          tempMessages.push({
            text: '',
            type: 'bot',
            isMarkdown: true,
            surveys: temporarySurveys,
          });
        }

        if (data && data?.history && data?.history.length === 0) {
          tempMessages.push({
            text:
              "Welcome to TGPS Assistant, your go-to guide for instant answers! 🌟 I'm here to help. Dive in and ask away",
            type: 'user',
          });
        }
        setResponseAdded(true);
        setMessages(tempMessages);
        setLoading(false);
      } else {
        setLoading(false);
      }
    },
  });

  useEffectOnlyOnce(() => {
    getlogkey();
  });

  function getAssignedSurveys(temporarySurveys: any) {
    let assignedSurveys;
    if (isLogin) {
      assignedSurveys = JSON.parse(
        getLocalStorage(KEY_LOCAL_ASSIGNED_SURVEYS)!,
      );
    } else {
      assignedSurveys = JSON.parse(
        getSessionStorage(KEY_LOCAL_ASSIGNED_SURVEYS)!,
      );
    }
    if (assignedSurveys != null && assignedSurveys.length > 0) {
      assignedSurveys.filter((survey: any) => {
        if (new Date() < new Date(survey.expiredDate))
          temporarySurveys.push(survey);
      });
      setAssignedSurvey(true);
      if (isLogin) {
        setDataLocalStorage(
          KEY_LOCAL_ASSIGNED_SURVEYS,
          JSON.stringify(assignedSurveys),
        );
      } else {
        setDataSessionStorage(
          KEY_LOCAL_ASSIGNED_SURVEYS,
          JSON.stringify(assignedSurveys),
        );
      }
    }
    return temporarySurveys;
  }

  useEffect(() => {
    if (message) handleUserInput(message, true);
  }, [location, message]);

  useEffect(() => {}, [messages]);

  useEffect(() => {
    if (chatBodyRef && chatBodyRef.current && responseAdded) scrollToBottom();
  }, [messages]);

  useEffectOnlyOnce(() => {
    if (chatBodyRef && chatBodyRef.current) scrollToBottom();
  });

  const scrollToBottom = () => {
    if (isMobile) {
      chatBodyRef!.current!.scrollTop = chatBodyRef!.current!.scrollHeight;
    } else {
      chatBodyRef!.current!.scrollTop = chatBodyRef!.current!.scrollHeight;
    }
  };

  useEffect(() => {
    if (historyKey && message) handleUserInput();
  }, [historyKey]);

  const handleUserInput = (message?: any, flg?: boolean) => {
    let input = flg !== undefined ? message : inputMessage;
    if (!input || input.trim() === '') {
      setLoading(false);
      return;
    }

    setLoading(true);

    setMessages(msgs => [
      ...msgs,
      { text: input, type: 'user' },
      { text: '', type: 'bot', streaming: true },
    ]);

    let currentText = '';
    let gotFirstResponse = false;
    let organizationId =
      meClient.organizationId || getSessionStorage(KEY_SESSION_ORGANIZATION_ID);

    var treatmentsAssigned = JSON.parse(
      getLocalStorage(KEY_LOCAL_ASSIGN_TREATMENT)! as string,
    );

    let params: QueryChatBotArgs['params'] = {
      query:
        flg != undefined
          ? message.replace(/\n/g, '')
          : inputMessage.replace(/\n/g, ''),
    };

    if (treatmentsAssigned != null && treatmentsAssigned.length > 0) {
      params['treatmentAssignmentInput'] = treatmentsAssigned;
      params['nologkey'] = treatmentAssigneeEmail
        ? treatmentAssigneeEmail
        : logKey != null && logKey != 'null'
        ? logKey
        : historyKey;
      if (!treatmentAssigneeEmail && !logKey) {
        setLogKey(historyKey);
        setDataLocalStorage(
          KEY_LOCAL_NO_LOG_KEY,
          params.nologkey ? params.nologkey : historyKey,
        );
      }
    } else if (logKey != null && logKey != 'null') {
      params['nologkey'] = logKey;
    } else if (!isLogin && (logKey == null || logKey == 'null')) {
      params['nologkey'] = historyKey;
      setLogKey(historyKey);
      setDataLocalStorage(KEY_LOCAL_NO_LOG_KEY, historyKey);
    }

    streamChatApi(
      organizationId,
      input.replace(/^\s+/, ''),
      params.nologkey || 'null',
      chunk => {
        currentText += chunk;
        // Only set loading to false on first real response
        if (!gotFirstResponse) {
          const { answer } = extractBotAnswerAndJson(currentText);
          if (answer && answer.trim() !== '') {
            setLoading(false);
            gotFirstResponse = true;
          }
        }
        setMessages(msgs => {
          const copy = [...msgs];
          const { answer, articles, responseId } = extractBotAnswerAndJson(
            currentText,
          );
          copy[copy.length - 1] = {
            ...copy[copy.length - 1],
            text: answer,
            streaming: true,
            relatedArticles: articles,
            responseId,
          };
          return copy;
        });
      },
      () => {
        if (input.replace(/^\s+/, '') === '/reset') {
          const { answer, articles, responseId } = extractBotAnswerAndJson(
            currentText,
          );
          setLoading(false);
          setMessages([
            { text: input, type: 'user' },
            {
              text: answer,
              type: 'bot',
              streaming: false,
              relatedArticles: articles,
              responseId,
            },
          ]);
          return;
        }

        // If for some reason the stream ends before a response, make sure loading is stopped
        setLoading(false);
        setMessages(msgs => {
          const copy = [...msgs];
          if (copy[copy.length - 1].streaming)
            copy[copy.length - 1] = {
              ...copy[copy.length - 1],
              streaming: false,
            };
          return copy;
        });
      },
      err => {
        setLoading(false);
        enqueueSnackbar('Error connecting to chatbot', { variant: 'error' });
        setMessages(msgs => msgs.filter(m => !m.streaming));
      },
      infoLines => setInfoLines(infoLines ?? []),
    );

    setInputMessage('');
  };

  const initiateSurvey = () => {
    if (inputMessage != '')
      setMessages([...messages, { text: inputMessage, type: 'user' }]);

    if (surveyInitiated == null) setSurveyInitiated(false);
  };

  useEffect(() => {
    if (chatbotDrawer && isMobile) {
      if (!displayChatbot) setChatbotDrawer(false);
      else setChatbotDrawer(true);
    }
  }, [isMobile, displayChatbot, setChatbotDrawer, chatbotDrawer]);

  const isTreatmentOrHomepage =
    window.location.pathname.includes('/treatment/') ||
    window.location.pathname.includes('/home/') ||
    window.location.pathname.includes('/section/') ||
    !pathExists;

  const handleSurveyOpen = (flag: boolean) => {
    setSurveyOpened(flag);
  };

  
  const [hasSentConnect, setHasSentConnect] = useState(false);

  useEffect(() => {
    if (chatbotDrawer && !hasSentConnect) {
      handleUserInput('/connect', true);
      setHasSentConnect(true);
    }
  }, [chatbotDrawer, hasSentConnect]);

  return (
    <>
      <ChatbotDrawerContext.Provider
        value={{
          messages,
          setMessages,
        }}
      >
        {isTreatmentOrHomepage && (
          <FloatingChatStyled
            color="primary"
            aria-label="add"
            onClick={() => {
              if (isMobile) setDisplayChatBot(!displayChatbot);
              setChatbotDrawer(!chatbotDrawer);
              if (dataMenuStatus?.menuStatus) {
                toogleMenuStatus({
                  variables: { status: !dataMenuStatus.menuStatus },
                });
              }
            }}
          >
            {chatbotDrawer ? <Cancel /> : <Chat />}
          </FloatingChatStyled>
        )}
        <DrawerStyled
          banner={dataBannerStatus?.bannerStatus?.toString()}
          surveyOpened={surveyOpened}
          anchor={'right'}
          open={!isMobile && chatbotDrawer && isTreatmentOrHomepage}
          variant="persistent"
          onClose={() => setChatbotDrawer(false)}
          style={{
            display:
              !isMobile && chatbotDrawer && isTreatmentOrHomepage
                ? 'block'
                : 'none',
          }}
        >
          <Chatbot
            assignedSurvey={assignedSurvey}
            setAssignedSurvey={setAssignedSurvey}
            loading={loading}
            messages={messages}
            setMessages={setMessages}
            chatBodyRef={chatBodyRef}
            inputMessage={inputMessage}
            setInputMessage={setInputMessage}
            handleUserInput={handleUserInput}
            selectedSlug={selectedSlug}
            setSelectedSlug={setSelectedSlug}
            surveyInitiated={surveyInitiated}
            initiateSurvey={initiateSurvey}
            setSurveyInitiated={setSurveyInitiated}
            selectedSurvey={selectedSurvey}
            setSelectedSurvey={setSelectedSurvey}
            fromAnotherComp={true}
            setResponseAdded={setResponseAdded}
            setTreatmentContextFilteredIds={setTreatmentContextFilteredIds}
            handleSurveyOpen={handleSurveyOpen}
            surveyOpened={surveyOpened}
            scrollToBottom={scrollToBottom}
            infoLines={infoLines}
          />
        </DrawerStyled>
        {isMobile && (
          <MobileChatBot
            assignedSurvey={assignedSurvey}
            setAssignedSurvey={setAssignedSurvey}
            loading={loading}
            messages={messages}
            setMessages={setMessages}
            chatBodyRef={chatBodyRef}
            inputMessage={inputMessage}
            setInputMessage={setInputMessage}
            handleUserInput={handleUserInput}
            selectedSlug={selectedSlug}
            setSelectedSlug={setSelectedSlug}
            surveyInitiated={surveyInitiated}
            setSurveyInitiated={setSurveyInitiated}
            selectedSurvey={selectedSurvey}
            setSelectedSurvey={setSelectedSurvey}
            initiateSurvey={initiateSurvey}
            displayChatbot={displayChatbot}
            setDisplayChatBot={setDisplayChatBot}
            setResponseAdded={setResponseAdded}
            scrollToBottom={scrollToBottom}
            handleSurveyOpen={handleSurveyOpen}
            surveyOpened={surveyOpened}
            infoLines={infoLines}
          />
        )}
      </ChatbotDrawerContext.Provider>
    </>
  );
};

export default React.memo(ChatbotDrawer);
